/**
 * Seeded determinism for the reliability engines.
 *
 * The engines never call a random number generator or the wall clock. Where a
 * choice has to look arbitrary — which of two operators drove a machine that
 * night, how a day's hours split across the two shifts, which shift an alarm
 * fell in — the choice is a pure function of a string key, so the same data
 * pack always produces the same attribution, in the browser and in
 * `scripts/oem/check-reliability.mjs` alike.
 *
 * Engine layer (layer 2 of the 4-layer architecture): pure functions.
 */

/** FNV-1a, 32-bit, over the UTF-16 code units of `key`. */
export function fnv1a(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * A uniform-looking number in `[0, 1)` from a key. A final avalanche round
 * decorrelates keys that differ only in their last character.
 */
export function unit(key: string): number {
  let h = fnv1a(key);
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 0x100000000;
}

/** Whole days from `from` to `to`, both `YYYY-MM-DD`. Pure date arithmetic. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** `YYYY-MM-DD` shifted by `days`. */
export function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** Round to `d` decimals, the way the demo's `fixed()` does. */
export const round = (n: number, d: number): number => +n.toFixed(d);
